import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { getInquiryV2QueryOptions } from '@/services/getInquiryV2';
import { isTwilioConversationId } from '@/util/isTwilioConversationId';

export type ConversationKind = 'inquiry' | 'support' | 'pending' | 'error';

const NOT_FOUND = 404;

function statusOf(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error) {
    const { status } = error as { status: unknown };

    return typeof status === 'number' ? status : undefined;
  }

  return undefined;
}

/**
 * Which screen a conversation id opens: an inquiry's chat or a support thread.
 *
 * A Twilio id is always an inquiry and is answered without a request. A native
 * id is a UUID, the same shape as a support thread's, so the id cannot say
 * which it is. The API answers: an inquiry that exists for the id is the chat,
 * and a 404 means the id is a support thread. The inquiry is read through the
 * query the chat itself reads, so a hit is not fetched twice.
 *
 * Any other failure is `error`, never `support`: guessing would open the
 * support thread screen on an inquiry because of a dropped connection.
 */
export function useConversationKind(conversationId: string | undefined): ConversationKind {
  const needsLookup = !!conversationId && !isTwilioConversationId(conversationId);

  const lookup = useQuery({
    ...getInquiryV2QueryOptions({ conversationId: conversationId ?? '' }),
    enabled: needsLookup,
    retry: (failureCount, error) => statusOf(error) !== NOT_FOUND && failureCount < 2,
  });

  const [decided, setDecided] = useState<{ id: string; kind: ConversationKind } | undefined>();

  const current = resolveKind(needsLookup, lookup);

  if (
    current !== 'pending' &&
    conversationId &&
    (decided?.id !== conversationId || decided.kind !== current)
  ) {
    setDecided({ id: conversationId, kind: current });
  }

  if (current === 'pending' && decided && decided.id === conversationId) {
    return decided.kind;
  }

  return current;
}

/**
 * The chat reads this same query and retries it, and a query that has never
 * loaded goes back to pending each time it refetches. Without holding the
 * decision, a lookup that failed would flip back to pending under the chat's
 * retries and the screen would sit blank, so the first answer stands until a
 * later one replaces it.
 */
function resolveKind(
  needsLookup: boolean,
  lookup: { isError: boolean; isSuccess: boolean; error: unknown; data: unknown }
): ConversationKind {
  if (!needsLookup) {
    return 'inquiry';
  }

  if (lookup.isError) {
    return statusOf(lookup.error) === NOT_FOUND ? 'support' : 'error';
  }

  if (lookup.isSuccess) {
    return lookup.data ? 'inquiry' : 'support';
  }

  return 'pending';
}
